import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type SyncSchedule, type UserOut } from "../../lib/api";
import { fmtDuration, fmtRange } from "../../lib/format";

const FROM_LAST_MAX_HOURS = 72;
const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"] as const;

export function DialpadSyncScheduleTile({
  me,
  lastCallAt,
  hasActive,
  isQueuing,
  onSyncFromLast,
  ingestError,
  syncButtonsDisabled,
  onToggleSyncButtonsDisabled,
  toggleError,
}: {
  me: UserOut;
  lastCallAt: string | null;
  hasActive: boolean;
  isQueuing: boolean;
  onSyncFromLast: (window: { start: string; end: string }) => void;
  ingestError: string | null;
  syncButtonsDisabled: boolean;
  onToggleSyncButtonsDisabled: (next: boolean) => void;
  toggleError: string | null;
}) {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newTime, setNewTime] = useState("09:00");
  const [newDays, setNewDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [newLabel, setNewLabel] = useState("");

  const schedulesQ = useQuery<SyncSchedule[]>({
    queryKey: ["dialpad-schedules"],
    queryFn: () => api.dialpadSchedules(),
    refetchInterval: 60_000,
  });

  const createM = useMutation({
    mutationFn: (body: {
      label?: string;
      time_of_day: string;
      days_of_week?: number[];
    }) => api.dialpadCreateSchedule(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dialpad-schedules"] });
      setNewTime("09:00");
      setNewDays([0, 1, 2, 3, 4, 5, 6]);
      setNewLabel("");
    },
  });

  const updateM = useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      label?: string;
      time_of_day?: string;
      days_of_week?: number[];
      is_enabled?: boolean;
    }) => api.dialpadUpdateSchedule(id, body),
    onSuccess: () =>
      qc.invalidateQueries({ queryKey: ["dialpad-schedules"] }),
  });

  const deleteM = useMutation({
    mutationFn: (id: string) => api.dialpadDeleteSchedule(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["dialpad-schedules"] });
      if (editingId) setEditingId(null);
    },
  });

  const computedWindow = useMemo(() => {
    const end = new Date();
    const maxStart = new Date(
      end.getTime() - FROM_LAST_MAX_HOURS * 3600 * 1000,
    );
    let start = maxStart;
    if (lastCallAt) {
      const last = new Date(lastCallAt);
      if (
        !Number.isNaN(last.getTime()) &&
        last.getTime() > maxStart.getTime()
      ) {
        start = last;
      }
    }
    return { start, end };
  }, [lastCallAt]);

  const schedules = schedulesQ.data ?? [];
  const syncDisabled = hasActive || isQueuing || syncButtonsDisabled;
  const isSupadmin = me.role === "supadmin";
  const canEdit = isSupadmin;

  const mutationError =
    createM.error?.message || updateM.error?.message || deleteM.error?.message;

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-md flex flex-col">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            Dialpad
          </div>
          <div className="font-medium text-foreground mt-1">
            DialPad Sync and Schedule
          </div>
        </div>
        {isSupadmin && (
          <div className="flex flex-col items-end gap-1">
            <label
              className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none"
              title="Super admin only. When on, disables the From last sync and Add schedule buttons for everyone who sees this tile."
            >
              <input
                type="checkbox"
                checked={syncButtonsDisabled}
                onChange={(e) =>
                  onToggleSyncButtonsDisabled(e.target.checked)
                }
                className="h-3.5 w-3.5"
              />
              <span className="text-foreground">Disable</span>
            </label>
            {toggleError && (
              <span className="text-xs text-destructive">{toggleError}</span>
            )}
          </div>
        )}
      </div>
      <p className="text-sm text-muted-foreground mt-1">
        One-click sync from your last logged call (capped at 72 hours).
        Dialpad users and groups are refreshed automatically at the start of
        every sync.
      </p>

      {/* From Last Sync button */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() =>
            onSyncFromLast({
              start: computedWindow.start.toISOString(),
              end: computedWindow.end.toISOString(),
            })
          }
          disabled={syncDisabled}
          title={
            syncButtonsDisabled
              ? "Currently disabled"
              : hasActive
                ? "A sync is already in progress"
                : undefined
          }
          className="rounded bg-primary text-primary-foreground text-sm px-4 py-2 hover:bg-primary/90 disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
        >
          {isQueuing
            ? "Queuing…"
            : hasActive
              ? "Sync in progress…"
              : "From last sync"}
        </button>
        <span className="text-xs text-muted-foreground">
          Will fetch{" "}
          <span className="text-foreground">
            {fmtRange(
              computedWindow.start.toISOString(),
              computedWindow.end.toISOString(),
            )}
          </span>{" "}
          (
          {fmtDuration(
            computedWindow.start.toISOString(),
            computedWindow.end.toISOString(),
          )}
          )
          {!lastCallAt && (
            <span className="ml-1">
              &mdash; no prior calls, using full 72h.
            </span>
          )}
        </span>
        {ingestError && (
          <span className="text-xs text-destructive">{ingestError}</span>
        )}
      </div>

      {/* Schedules section */}
      <div className="mt-5 pt-4 border-t border-border">
        <div>
          <div className="text-sm font-medium text-foreground">
            Schedules
          </div>
          <div className="text-xs text-muted-foreground">
            Schedule syncs no less than three hours apart. All times
            are NZ time (Pacific/Auckland).
          </div>
        </div>

        {mutationError && (
          <div className="mt-2 text-xs text-destructive">{mutationError}</div>
        )}

        {schedules.length === 0 ? (
          <div className="mt-3 rounded border border-dashed border-border bg-muted/20 px-3 py-4 text-sm text-muted-foreground">
            {schedulesQ.isLoading
              ? "Loading schedules…"
              : "No schedules yet."}
          </div>
        ) : (
          <ul className="mt-3 space-y-2">
            {schedules.map((s) => (
              <ScheduleRow
                key={s.id}
                schedule={s}
                isEditing={editingId === s.id}
                canEdit={canEdit}
                onToggleEdit={() =>
                  setEditingId(editingId === s.id ? null : s.id)
                }
                onUpdate={(body) => updateM.mutate({ id: s.id, ...body })}
                onDelete={() => deleteM.mutate(s.id)}
                isUpdating={updateM.isPending}
                isDeleting={deleteM.isPending}
              />
            ))}
          </ul>
        )}
      </div>

      {/* New schedule preview (for setting time/days before adding) */}
      {canEdit && (
        <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
          <span>New schedule:</span>
          <input
            type="time"
            value={newTime}
            onChange={(e) => setNewTime(e.target.value)}
            className="border border-input rounded px-2 py-1 text-sm"
          />
          <DayOfWeekPicker
            days={newDays}
            onChange={setNewDays}
            disabled={false}
          />
          <input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Label (optional)"
            className="border border-input rounded px-2 py-1 text-sm w-32"
          />
          <div className="ml-auto">
            <button
              type="button"
              onClick={() => {
                createM.mutate({
                  label: newLabel || undefined,
                  time_of_day: newTime,
                  days_of_week: newDays,
                });
              }}
              disabled={syncButtonsDisabled || createM.isPending}
              title={
                syncButtonsDisabled ? "Currently disabled" : "Add a schedule"
              }
              className="rounded bg-primary text-primary-foreground text-sm px-3 py-1.5 hover:bg-primary/90 disabled:opacity-50 transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)]"
            >
              {createM.isPending ? "Adding…" : "Add schedule"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ScheduleRow({
  schedule: s,
  isEditing,
  canEdit,
  onToggleEdit,
  onUpdate,
  onDelete,
  isUpdating,
  isDeleting,
}: {
  schedule: SyncSchedule;
  isEditing: boolean;
  canEdit: boolean;
  onToggleEdit: () => void;
  onUpdate: (body: {
    label?: string;
    time_of_day?: string;
    days_of_week?: number[];
    is_enabled?: boolean;
  }) => void;
  onDelete: () => void;
  isUpdating: boolean;
  isDeleting: boolean;
}) {
  const [editTime, setEditTime] = useState(s.time_of_day);
  const [editDays, setEditDays] = useState<number[]>(s.days_of_week);
  const [editLabel, setEditLabel] = useState(s.label ?? "");

  const controlsDisabled = !canEdit || isUpdating || isDeleting;

  return (
    <li className="flex flex-wrap items-center gap-3 rounded border border-border bg-card/60 px-3 py-2">
      {/* Enable/disable toggle */}
      <label className="flex items-center gap-1.5 text-xs text-muted-foreground select-none">
        <input
          type="checkbox"
          checked={s.is_enabled}
          onChange={(e) => onUpdate({ is_enabled: e.target.checked })}
          disabled={controlsDisabled}
          className="h-3.5 w-3.5"
        />
        <span className="text-foreground">
          {s.is_enabled ? "On" : "Off"}
        </span>
      </label>

      {/* System badge */}
      {s.is_system && (
        <span className="rounded bg-muted text-muted-foreground text-[10px] px-1.5 py-0.5 uppercase tracking-wide font-medium">
          System
        </span>
      )}

      {/* Label */}
      {isEditing && !s.is_system ? (
        <input
          type="text"
          value={editLabel}
          placeholder="Label (optional)"
          onChange={(e) => setEditLabel(e.target.value)}
          className="border border-input rounded px-2 py-1 text-sm w-32"
        />
      ) : (
        <div className="text-sm text-foreground min-w-[6rem]">
          {s.label ? (
            s.label
          ) : (
            <span className="text-muted-foreground italic">(no label)</span>
          )}
        </div>
      )}

      {/* Time picker */}
      <input
        type="time"
        value={isEditing && !s.is_system ? editTime : s.time_of_day}
        disabled={!isEditing || s.is_system}
        onChange={(e) => setEditTime(e.target.value)}
        className="border border-input rounded px-2 py-1 text-sm disabled:bg-muted/30 disabled:text-muted-foreground"
      />

      {/* Day-of-week picker */}
      <DayOfWeekPicker
        days={isEditing ? editDays : s.days_of_week}
        onChange={setEditDays}
        disabled={!isEditing}
      />

      {/* Strategy indicator */}
      <span className="text-[10px] text-muted-foreground">
        {s.window_strategy === "fixed_lookback"
          ? `${s.lookback_minutes}min`
          : "72h cap"}
      </span>

      {/* Last fired */}
      {s.last_fired_at && (
        <span className="text-[10px] text-muted-foreground" title="Last fired">
          Last:{" "}
          {new Date(s.last_fired_at).toLocaleString("en-NZ", {
            timeZone: "Pacific/Auckland",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })}
        </span>
      )}

      {/* Action buttons */}
      <div className="ml-auto flex items-center gap-2">
        {canEdit && (
          <>
            <button
              type="button"
              onClick={() => {
                if (isEditing) {
                  onUpdate({
                    ...(s.is_system
                      ? { days_of_week: editDays }
                      : {
                          label: editLabel || undefined,
                          time_of_day: editTime,
                          days_of_week: editDays,
                        }),
                  });
                }
                onToggleEdit();
              }}
              disabled={controlsDisabled}
              className="rounded border border-input bg-card text-foreground text-xs px-2 py-1 hover:bg-muted/40 disabled:opacity-50"
            >
              {isEditing ? "Save" : "Edit"}
            </button>
            {!s.is_system && (
              <button
                type="button"
                onClick={onDelete}
                disabled={controlsDisabled}
                className="rounded border border-input bg-card text-destructive text-xs px-2 py-1 hover:bg-destructive/10 disabled:opacity-50"
              >
                Delete
              </button>
            )}
          </>
        )}
      </div>
    </li>
  );
}

function DayOfWeekPicker({
  days,
  onChange,
  disabled,
}: {
  days: number[];
  onChange: (days: number[]) => void;
  disabled: boolean;
}) {
  const toggle = (day: number) => {
    if (disabled) return;
    const next = days.includes(day)
      ? days.filter((d) => d !== day)
      : [...days, day].sort();
    if (next.length > 0) onChange(next);
  };

  return (
    <div className="flex gap-0.5">
      {DAY_LABELS.map((label, i) => {
        const active = days.includes(i);
        return (
          <button
            key={i}
            type="button"
            onClick={() => toggle(i)}
            disabled={disabled}
            title={
              ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"][i]
            }
            className={`w-7 h-7 rounded text-xs font-medium transition-colors duration-[var(--duration-base)] ease-[var(--easing-standard)] disabled:opacity-50 ${
              active
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground"
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
